<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import generateClassNames from '../_mixins/generate-enum-classes';
import NtvTooltip from '../Tooltip';

import { availableIcons } from './icons';
export { availableIcons };

/**
 * Vue wrapper for our svg icons. Mostly a wrapper around font-awesome icons
 * with some custom icons.
 */
export default {
  name: 'NtvIcon',

  mixins: [
    /**
     * Injects computed:
     *  classes
     *
     * Injects methods:
     *  generateClassName(prop, val)
     */
    generateClassNames
  ],

  components: {
    FontAwesomeIcon
  },

  directives: {
    tooltip: NtvTooltip.VTooltip
  },

  props: {
    /**
     * Set name of the SVG icon to use, _without_ suffix (.svg)
     */
    name: {
      type: String,
      default: ''
    },
    /**
     * Set the size of svg icon to use from the list else can be overridden using width and height props
     */
    size: {
      type: String,
      default: ''
    },
    /**
     * Set color of the SVG
     */
    color: {
      type: String,
      default: 'dark'
    },
    /**
     * Set or override SVG element's fill color
     */
    fill: {
      type: String,
      default: '#fff'
    },
    /**
     * Set height in px/em
     */
    height: {
      type: String,
      default: ''
    },
    /**
     * Set width in px/em
     */
    width: {
      type: String,
      default: ''
    },
    /**
     * Flag to use the third party vue-font-awesome component instead. If this
     * is set to true, the type property must be used in conjunction.
     */
    useFa: {
      type: Boolean,
      default: false
    },
    /**
     * The type of font awesome icon library to pull from. To use this,
     * useFa must be set to true.
     */
    type: {
      type: String,
      default: 'light'
    },
    /**
     * Whether to set a fixed width on the icon. To use this, useFa must be set
     * to true.
     */
    fixedWidth: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to spin the icon or not, To use this, useFa must be set to true.
     */
    spin: {
      type: Boolean,
      default: false
    },
    rotation: {
      type: Number,
      default: null
    },
    /**
     * Transform svg state
     * (https://github.com/FortAwesome/vue-fontawesome#advanced)
     */
    transform: {
      type: String,
      default: ''
    },
    /**
     * Whether the icon is disabled or not
     */
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Add toolTip
     */
    toolTip: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      faIcon: null,
      dToolTip: null
    };
  },

  watch: {
    toolTip: {
      immediate: true,
      handler(newValue) {
        this.dToolTip = newValue;
      }
    },
    type: {
      immediate: true,
      handler() {
        this.getFaIcon();
      }
    },
    name: {
      immediate: true,
      handler() {
        this.getFaIcon();
      }
    }
  },

  methods: {
    // Font awesome specific computed classes.
    getFaIcon() {
      if (this.name) {
        // Capitialize the first letter of the property "name" as camelCase is
        // used in file names.
        const type = this.type[0].toUpperCase() + this.type.substr(1);
        const iconImportFunction = availableIcons[`${this.name}${type}`];

        // Use code-splitting to dynamically load the desired icon.
        if (iconImportFunction) {
          availableIcons[`${this.name}${type}`].then((data) => {
            this.faIcon = data.definition;
          });
        }
      }
    },
    onClick(event) {
      this.$emit('click', event);
    }
  }
};
</script>

<template>
  <font-awesome-icon
    v-tooltip="dToolTip"
    :class="classes"
    :fixed-width="fixedWidth"
    :icon="faIcon"
    :rotation="rotation"
    :spin="spin"
    :transform="transform"
    @click="onClick"
    class="ntv-icon"
    v-if="faIcon && useFa"
  />
</template>

<style lang="scss">
@import '../../theme/theme.scss';

.ntv-icon {
  display: inline-block;
  & > svg {
    fill: currentColor !important;
  }
}

.nui-fa-disabled {
  color: $--grey-5;
  pointer-events: none;
}
</style>
